import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BookOpen, FileText, ListChecks, ShieldCheck, Sparkles } from 'lucide-react';
import api from '@/lib/api';
import ScopeSectionsTab from './library/ScopeSectionsTab';
import ExclusionsTab from './library/ExclusionsTab';
import ProposalDefaultsTab from './library/ProposalDefaultsTab';

// ─── Completeness Banner ───────────────────────────────────────────────
// Clicking the banner toggles the "incomplete only" filter on the sections
// tab. Uses a controlled prop passed down from the page.
function CompletenessBanner({ counts, incompleteFilter, onToggleIncomplete }) {
  const total = counts?.total ?? 0;
  const complete = counts?.complete ?? 0;
  const allComplete = total > 0 && complete === total;
  const tone = allComplete ? 'complete' : 'incomplete';
  return (
    <button
      type="button"
      onClick={() => onToggleIncomplete(!incompleteFilter)}
      className={`w-full text-left rounded-[4px] px-4 py-3 border transition-colors ${
        tone === 'complete'
          ? 'border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10'
          : 'border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10'
      }`}
      data-testid="library-completeness-banner"
    >
      <div className="flex items-center gap-3">
        {tone === 'complete' ? (
          <ShieldCheck className="h-4 w-4 text-emerald-500" />
        ) : (
          <Sparkles className="h-4 w-4 text-amber-500" />
        )}
        <div className="text-sm">
          <span className={tone === 'complete' ? 'text-emerald-500' : 'text-amber-500'}>
            {complete} of {total} sections have complete expected values set.
          </span>
          {!allComplete && (
            <span className="ml-2 text-muted-foreground text-xs">
              {counts.missing_fee} missing fee, {counts.missing_hours} missing hours -
              click to {incompleteFilter ? 'clear filter' : 'show incomplete only'}.
            </span>
          )}
          {allComplete && (
            <span className="ml-2 text-muted-foreground text-xs">Every active section has a fee and hours.</span>
          )}
        </div>
      </div>
    </button>
  );
}

// ─── SOPs placeholder ───────────────────────────────────────────────────
function SopsPlaceholder() {
  return (
    <Card className="border-border bg-card shadow-none">
      <CardContent className="p-12 text-center">
        <BookOpen className="h-8 w-8 text-muted-foreground/40 mx-auto mb-3" />
        <div className="text-sm font-medium">Standard Operating Procedures</div>
        <div className="text-xs text-muted-foreground mt-2">
          Standard operating procedures and process guides. Coming in a future update.
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main Library Page ─────────────────────────────────────────────────
export default function LibraryPage() {
  const { user } = useAuth();
  const canEdit = user?.role === 'Administrator' || user?.role === 'Owner';
  const [topTab, setTopTab] = useState('proposal-library');
  const [subTab, setSubTab] = useState('scope-sections');
  const [completeness, setCompleteness] = useState({ total: 0, complete: 0, missing_fee: 0, missing_hours: 0 });
  const [incompleteFilter, setIncompleteFilter] = useState(false);

  const loadCompleteness = () => {
    api.get('/section-library/completeness').then(r => setCompleteness(r.data)).catch(() => {});
  };

  useEffect(() => { loadCompleteness(); }, []);

  const handleToggleIncomplete = (v) => {
    setIncompleteFilter(v);
    if (v) setSubTab('scope-sections');
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="library-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-baseline gap-3 text-[22px] font-medium tracking-tight">
            Library
          </h1>
        </div>
      </div>

      {/* Top-level tabs: Proposal Library | SOPs */}
      <Tabs value={topTab} onValueChange={setTopTab}>
        <TabsList>
          <TabsTrigger value="proposal-library" className="text-xs" data-testid="library-top-tab-proposal">
            <FileText className="h-3 w-3 mr-1" /> Proposal Library
          </TabsTrigger>
          <TabsTrigger value="sops" className="text-xs" data-testid="library-top-tab-sops">
            <BookOpen className="h-3 w-3 mr-1" /> SOPs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="proposal-library" className="mt-3 space-y-3">
          <CompletenessBanner
            counts={completeness}
            incompleteFilter={incompleteFilter}
            onToggleIncomplete={handleToggleIncomplete}
          />

          <Tabs value={subTab} onValueChange={setSubTab}>
            <TabsList>
              <TabsTrigger value="scope-sections" className="text-xs" data-testid="library-subtab-sections">
                <ListChecks className="h-3 w-3 mr-1" /> Scope Sections
              </TabsTrigger>
              <TabsTrigger value="exclusions" className="text-xs" data-testid="library-subtab-exclusions">
                Exclusions
              </TabsTrigger>
              <TabsTrigger value="defaults" className="text-xs" data-testid="library-subtab-defaults">
                Proposal Defaults
              </TabsTrigger>
            </TabsList>

            <TabsContent value="scope-sections" className="mt-3">
              <ScopeSectionsTab
                canEdit={canEdit}
                incompleteFilter={incompleteFilter}
                onIncompleteFilterChange={setIncompleteFilter}
                onDataChanged={loadCompleteness}
                userRole={user?.role}
              />
            </TabsContent>

            <TabsContent value="exclusions" className="mt-3">
              <ExclusionsTab canEdit={canEdit} />
            </TabsContent>

            <TabsContent value="defaults" className="mt-3">
              <ProposalDefaultsTab canEdit={canEdit} />
            </TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="sops" className="mt-3">
          <SopsPlaceholder />
        </TabsContent>
      </Tabs>
    </div>
  );
}
